import { createTheme, ThemeOptions } from '@mui/material/styles';

export const huihangColors = {
  primary: '#0c447c',
  primaryLight: '#1a5fbf',
  primaryDark: '#042c53',
  primaryGlow: '#378add',
  link: '#185fa5',
  textPrimary: '#042c53',
  textSecondary: '#2c4a72',
  textMuted: '#5a8aca',
  bgWhite: '#ffffff',
  bgSidebar: '#f5f9ff',
  bgSearch: '#f0f4f8',
  bgTableHead: '#e8f0fc',
  bgSelected: '#dceefe',
  bgBadge: '#a8c4e8',
  bgHover: '#f0f4f8',
  border: '#a8c4e4',
  borderFocus: '#1a5fbf',
  borderHover: '#5a8aca',
  success: '#2e7d32',
  successLight: '#e8f5e9',
  warning: '#f57f17',
  warningLight: '#fff8e1',
  error: '#d32f2f',
  errorLight: '#ffebee',
};

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: huihangColors.primary,
      light: huihangColors.primaryLight,
      dark: huihangColors.primaryDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: huihangColors.textSecondary,
      light: huihangColors.textMuted,
      dark: huihangColors.primaryDark,
      contrastText: '#ffffff',
    },
    success: {
      main: huihangColors.success,
      light: huihangColors.successLight,
      dark: '#1b5e20',
    },
    warning: {
      main: huihangColors.warning,
      light: huihangColors.warningLight,
      dark: '#e65100',
    },
    error: {
      main: huihangColors.error,
      light: huihangColors.errorLight,
      dark: '#b71c1c',
    },
    info: {
      main: huihangColors.primary,
      light: huihangColors.bgSelected,
      dark: huihangColors.primaryDark,
    },
    background: {
      default: huihangColors.bgSidebar,
      paper: huihangColors.bgWhite,
    },
    text: {
      primary: huihangColors.textPrimary,
      secondary: huihangColors.textSecondary,
      disabled: huihangColors.textMuted,
    },
    divider: huihangColors.border,
    action: {
      hover: huihangColors.bgHover,
      selected: huihangColors.bgSelected,
      disabled: '#a8b7c9',
      disabledBackground: huihangColors.bgTableHead,
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.4 },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.5 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    button: { textTransform: 'none', fontWeight: 500 },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  components: {
    MuiAvatar: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        circular: {
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          backgroundColor: huihangColors.primary,
          '&:hover': {
            backgroundColor: huihangColors.primaryLight,
            boxShadow: '0 2px 8px rgba(12, 68, 124, 0.25)',
          },
        },
        containedPrimary: {
          backgroundColor: huihangColors.primary,
          '&:hover': { backgroundColor: huihangColors.primaryLight },
        },
        outlinedPrimary: {
          borderColor: huihangColors.primaryLight,
          color: huihangColors.primary,
          '&:hover': {
            borderColor: huihangColors.primary,
            backgroundColor: huihangColors.bgSelected,
          },
        },
        sizeSmall: { padding: '4px 12px', fontSize: '0.875rem' },
        sizeLarge: { padding: '12px 24px', fontSize: '1rem' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(4, 44, 83, 0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 8 },
        elevation1: { boxShadow: '0 1px 3px rgba(4, 44, 83, 0.08)' },
        elevation2: { boxShadow: '0 2px 8px rgba(4, 44, 83, 0.10)' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: huihangColors.bgWhite,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: huihangColors.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: huihangColors.borderHover,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: huihangColors.borderFocus,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: huihangColors.primaryLight },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: huihangColors.textSecondary,
          '&.Mui-selected': { color: huihangColors.primaryLight },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: huihangColors.border },
        head: {
          fontWeight: 600,
          backgroundColor: huihangColors.bgTableHead,
          color: huihangColors.textPrimary,
          borderColor: huihangColors.border,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.Mui-selected': { backgroundColor: huihangColors.bgSelected },
          '&.Mui-selected:hover': { backgroundColor: huihangColors.bgSelected },
          '&:hover': { backgroundColor: huihangColors.bgHover },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: huihangColors.textMuted,
          '&.Mui-checked': { color: huihangColors.primaryLight },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: huihangColors.primaryLight,
            color: '#ffffff',
          },
          '&.Mui-selected:hover': {
            backgroundColor: huihangColors.primary,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16 },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          backgroundColor: huihangColors.bgSidebar,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(4, 44, 83, 0.95)',
          borderRadius: 6,
          fontSize: '0.75rem',
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);

export const darkTheme = createTheme({
  ...themeOptions,
  palette: {
    ...themeOptions.palette,
    mode: 'dark',
    background: {
      default: '#1b2a3d',
      paper: '#24364d',
    },
    text: {
      primary: '#f5f9ff',
      secondary: '#dceefe',
      disabled: '#a8c4e4',
    },
    divider: '#5a8aca',
  },
});
